import { Typography, useMediaQuery, Box, Grid, Button } from '@mui/material';
import { useTheme } from '@mui/styles';
import { MainProps } from '@/components/NonInviteFlow/Main';
import GraphQLErrors from '@/components/GraphQLErrors';
import MultiStepForm, { FormStep } from '@/components/MultiStepForm';
import PersonalDetailsForm from '../SignupForms/PersonalDetailsForm';
import SiteDetailsForm from '../SignupForms/SiteDetailsForm';
import { FormikProps, FormikValues } from 'formik';
import {
  formIntialState,
  personalDetailsValidationSchema,
  inviteFlowSiteDetailsValidationSchema,
} from '@/constants/formInitConstants';
import Confirmation from '@/components/InviteFlow/Confirmation';
import { useRouter } from 'next/router';
import { useMultiStepFormContext } from '@/context/MultiStepFormContext';
import { SEARCH_SIMILAR_SITES } from '@/queries';
import DuplicatesScreen from '@/components/NonInviteFlow/Duplicates';
import { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { TRACK_USER_EVENT } from '@/mutations';
import { uiOnlyFields } from '@/constants/formInitConstants';
import { UIOnlyFields } from '@/types';
import {
  SiteVaultInvitationValidation,
  SiteApplicationInput,
  SearchSimilarSitesQuery,
  SearchSimilarSitesQueryVariables,
  Site,
  TrackUserEventMutation,
  TrackUserEventMutationVariables,
} from '@/gql/graphql';
import { TrackingEventsConstants } from '@/constants';
import FAQSection from '@/components/FAQSection';

interface InviteFlowMainProps extends MainProps {
  invitationData: SiteVaultInvitationValidation;
}

const Main = ({
  invitationData,
  graphQLErrors,
  loading,
  onSubmit,
  isConfirmation,
  applicationId,
  haveSiteVaultFlow = false,
}: InviteFlowMainProps) => {
  const router = useRouter();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const formContext = useMultiStepFormContext();
  const [showDuplicatesScreen, setShowDuplicatesScreen] = useState(false);
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [selectedDuplicate, setSelectedDuplicate] = useState(false);

  const [searchDuplicateSites, { data, loading: duplicatesLoading }] = useLazyQuery<
    SearchSimilarSitesQuery,
    SearchSimilarSitesQueryVariables
  >(SEARCH_SIMILAR_SITES);

  const [fireTrackEvent, { loading: trackEventLoading }] = useMutation<
    TrackUserEventMutation,
    TrackUserEventMutationVariables
  >(TRACK_USER_EVENT);

  const initialValues = {
    ...formIntialState,
    ...formContext?.formSnapshot,
    ...{
      email: invitationData.email,
      marketingSource: 'SiteVault Inviter: Sponsor invitation',
      referralSource: invitationData.sponsorName,
      emailOptIn: false,
    },
  };

  const study = (
    <Box component="span">
      for Study{' '}
      <Box component="span" sx={{ fontWeight: 700 }}>
        {invitationData.studyName}
      </Box>
    </Box>
  );

  if (isConfirmation || selectedDuplicate) {
    return (
      <Confirmation
        isInviteFlow
        sponsorName={invitationData.sponsorName as string}
        fromDuplicateApp={selectedDuplicate}
        applicationId={applicationId}
      />
    );
  }

  if (showDuplicatesScreen) {
    return (
      <DuplicatesScreen
        loading={submittingApplication}
        similarSites={data?.searchSimilarSites?.sites as Site[]}
        onDismiss={() => onSubmit(formContext?.formSnapshot as FormikValues)}
        onSelected={(usn) => {
          setSubmittingApplication(true);
          const vals = { ...formContext?.formSnapshot } as SiteApplicationInput;
          Object.keys(uiOnlyFields).forEach((field) => delete vals[field]);
          vals.duplicateUsn = usn;
          vals.inviteId = router.query.code as string;
          void (() => {
            onSubmit(vals, usn, () => {
              setSelectedDuplicate(true);
              setSubmittingApplication(false);
              setShowDuplicatesScreen(false);
            });
          })();
        }}
      />
    );
  }

  return (
    <>
      <Box
        textAlign="center"
        mt={mdUp ? 9 : 4}
        mb={7}
        sx={{ color: (theme) => theme.palette.grey[800] }}>
        {haveSiteVaultFlow ? (
          <Box
            textAlign="center"
            mt={mdUp ? 9 : 4}
            mb={7}
            sx={{ color: (theme) => theme.palette.grey[800] }}>
            <Typography variant="h1" sx={{ fontWeight: 500 }} color="inherit">
              Get access to your SiteVault
            </Typography>
            <Typography
              variant="h4"
              sx={{ marginTop: (theme) => theme.spacing(1) }}
              color="inherit">
              Enter your information to help identify your site
            </Typography>
          </Box>
        ) : (
          <Typography variant="h2" sx={{ fontWeight: 500 }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              {invitationData.sponsorName}
            </Box>{' '}
            would like your research site to use SiteVault Study Connect{' '}
            {invitationData.studyName && study}
          </Typography>
        )}
      </Box>

      <Grid container spacing={2}>
        {!haveSiteVaultFlow && <Grid item md={2} xs={12}></Grid>}
        <Grid item md={haveSiteVaultFlow ? 7 : 8} xs={12}>
          <GraphQLErrors graphQLErrors={graphQLErrors} />
          <Box mt={2}>
            <MultiStepForm
              initialValues={initialValues}
              isSubmitting={loading || duplicatesLoading}
              onSubmit={async (values) => {
                formContext?.setFormSnapshot(values);
                const { data } = await searchDuplicateSites({
                  variables: {
                    query: {
                      company: values.organization as string,
                      email: values.email,
                      address: values.address1,
                      city: values.city,
                      country: values.country,
                      state: values.stateProvince,
                      postalCode: values.postalCode,
                      department: values.name,
                    },
                  },
                });

                if (data?.searchSimilarSites && data.searchSimilarSites.total > 0) {
                  setShowDuplicatesScreen(true);
                } else {
                  onSubmit(values);
                }
              }}
              displayStepper={false}
              lastStepCTALabel="Submit">
              <FormStep name="Personal Details" validationSchema={personalDetailsValidationSchema}>
                {(formik: FormikProps<SiteApplicationInput>) => {
                  return <PersonalDetailsForm isInviteFlow formik={formik} />;
                }}
              </FormStep>

              <FormStep
                name="Site Details"
                validationSchema={inviteFlowSiteDetailsValidationSchema}>
                {(formik: FormikProps<UIOnlyFields & SiteApplicationInput>) => {
                  return <SiteDetailsForm formik={formik} isInviteFlow />;
                }}
              </FormStep>
            </MultiStepForm>
          </Box>

          {!haveSiteVaultFlow && (
            <Box my={8} display="flex" justifyContent="center">
              <Box
                maxWidth={525}
                height={mdUp ? 'auto' : 75}
                width="100%"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexDirection={mdUp ? 'row' : 'column'}>
                <Typography color="text.secondary" variant="h6">
                  Does your site already have SiteVault?
                </Typography>
                <Button
                  variant="text"
                  sx={{ px: 0 }}
                  color="info"
                  disabled={trackEventLoading}
                  onClick={async () => {
                    await fireTrackEvent({
                      variables: {
                        eventType: TrackingEventsConstants.SV_CLICK_EVENT,
                        eventValue: 'Learn how to get access',
                        trackingId: null,
                        url: null,
                      },
                    });
                    void router.push(
                      `/?code=${router.query.code as string}&alreadyHaveSiteVault=true`
                    );
                  }}>
                  Learn how to get access
                </Button>
              </Box>
            </Box>
          )}
        </Grid>
        {haveSiteVaultFlow && (
          <Grid item md={5} sm={12} xs={12}>
            <FAQSection />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default Main;
